/**
 * Public sign-up error copy + the pre-submit business-email check.
 *
 * ⭐⭐ THE DEFECT THIS MODULE EXISTS TO MAKE UNREPEATABLE (13 August 2026).
 * `SowEmbedPage` rendered `data.error` — the MACHINE CODE — into its persistent
 * `role="alert"` box. A paying stakeholder, mid-demo, was shown the literal
 * string `email_domain_blocked`, and it stayed on screen until the next submit
 * (the box is deliberately non-dismissing). The friendly copy the server was
 * ALREADY sending in `data.message` sat one property away, unread.
 *
 * ⚠ THE RULE IS ABSOLUTE: `data.error` IS NEVER RENDERED. It is a routing key,
 * not copy. {@link resolveSignupError} is the only path from a response body to
 * on-screen text, and it has no branch that can return a raw code — the worst
 * case is the generic fallback. That is what makes "the server forgot a message
 * for a new code" degrade to a vague-but-human sentence instead of regressing to
 * a stack-trace-shaped string.
 */

/** The shape the sign-up + beta routes answer with. Both fields optional — the
 *  resolver's job is precisely to cope when they are absent. */
export interface SignupErrorBody {
  error?: string;
  message?: string;
}

/**
 * Client-side fallback copy, keyed by server error code.
 *
 * ⚠ THIS IS A FALLBACK, NOT THE SOURCE OF TRUTH. The server owns the copy
 * (`SIGNUP_ERROR_MESSAGES` in `lambdas/signup-api/src/index.ts`) so one edit
 * changes every caller. This map exists for two cases that are real rather than
 * theoretical: a response that never reached the server at all (the edge 403 /
 * circuit-breaker paths, which can answer without a body), and a browser holding
 * a cached bundle older than a server-side copy change.
 */
const FALLBACK_COPY: Record<string, string> = {
  email_domain_blocked:
    "To sign up for Nexanomics, you'll need an email address on your own business domain. "
    + "We don't support personal email addresses (like Gmail, Yahoo, Outlook.com, iCloud, or "
    + 'Hotmail) — please use your work email instead.',
  invalid_email: "That email address doesn't look right. Please check it and try again.",
  missing_fields: 'Please complete every required field before continuing.',
  invalid_json: 'We couldn’t read that submission. Please try again.',
  turnstile_failed: 'The verification challenge didn’t complete. Please try the challenge again.',
  email_already_exists: 'An account with this email already exists. Please sign in instead.',
  sow_gate_closed:
    'Sign-ups aren’t open right now. Please register your interest and we’ll let you know as '
    + 'soon as they re-open.',
  service_unavailable: 'Sign-up is temporarily unavailable. Please try again in a few minutes.',
  server_error: 'Something went wrong on our side. Please try again in a moment.',
  // ADR-073 Am.5 — the card-first payment-confirm (`/signup/applications/pay`, account service).
  // Mirrors `SIGNUP_PAY_MESSAGES` in lambdas/account/src/signup-pay.ts; every refusal near money
  // says nothing was charged.
  invalid_domain: 'Please enter your website domain, e.g. yourbusiness.com.',
  stale_client: 'This page is out of date. Please refresh it and try again.',
  missing_plan: 'Please choose a plan to continue.',
  lead_not_found:
    'We couldn’t find this sign-up — it may have expired. If you’ve already paid, check your email for your '
    + 'activation link; otherwise please start again.',
  payment_in_progress: 'Your payment is already being processed. Please check your email in a moment.',
  domain_already_registered:
    'That website is already connected to a Nexanomics account, so you haven’t been charged. If it’s yours, '
    + 'sign in instead — or go back and use a different website.',
  payment_declined:
    'Your payment was declined and you haven’t been charged. Please check your card details and try again.',
};

export const GENERIC_SIGNUP_ERROR = 'Sign-up failed. Please try again.';

/**
 * Response body → the sentence a human reads. Order: the server's own copy, then
 * this module's fallback for the code, then a generic line.
 *
 * ⚠ A `message` IS ONLY TRUSTED WHEN IT IS PLAUSIBLY PROSE. A server that
 * mistakenly echoes its code into `message` would otherwise put the code back on
 * screen through the very function written to keep it off — so a value that
 * looks like a machine token (`snake_case`, no spaces) is rejected and falls
 * through to the map. This is cheap and it closes the loop properly: there is no
 * input to this function that can produce a raw code as output.
 */
export function resolveSignupError(body: SignupErrorBody | null | undefined): string {
  const message = typeof body?.message === 'string' ? body.message.trim() : '';
  if (message && looksLikeProse(message)) return message;
  const code = typeof body?.error === 'string' ? body.error.trim() : '';
  if (code && FALLBACK_COPY[code]) return FALLBACK_COPY[code];
  return GENERIC_SIGNUP_ERROR;
}

/** Prose, not a token: contains whitespace and is not a bare `snake_case` slug. */
function looksLikeProse(s: string): boolean {
  if (!/\s/.test(s)) return false;
  return !/^[a-z0-9]+(_[a-z0-9]+)+$/.test(s);
}

/**
 * Free-provider domains blocked from the paid sign-up.
 *
 * ⚠ A COPY-FORK OF THE SERVER LIST, GUARDED AGAINST DRIFT BY CI. The portal and
 * `lambdas/signup-api` are separate build roots with no shared import path, so
 * this is the catalogue model (copy-fork) rather than a shared module. A copied
 * constant drifts silently, so `signup-error-copy.test.ts` parses BOTH lists out
 * of source and fails the build if they disagree.
 *
 * ⭐ AND THE CHECK IT FEEDS IS ADVISORY ONLY — it never blocks a submit. The
 * server is the authority (it runs this same list before anything else). If this
 * list ever fell BEHIND, the visitor simply learns at submit instead of at blur;
 * if it ever ran AHEAD, a warning shows on an address the server would accept —
 * which is why the copy is a warning the visitor can submit past, not a block.
 */
export const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.com.au', 'yahoo.co.uk', 'yahoo.co.jp',
  'hotmail.com', 'hotmail.com.au', 'hotmail.co.uk',
  'outlook.com', 'outlook.com.au',
  'live.com', 'live.com.au', 'live.co.uk',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me',
  'mail.com', 'email.com',
  'zoho.com',
  'ymail.com',
  'msn.com',
  'inbox.com',
  'fastmail.com',
  'tutanota.com',
]);

/**
 * On-blur check for the business-email field: `null` when there is nothing to
 * say, otherwise the warning to show.
 *
 * ⭐ WHY BLUR AND NOT SUBMIT (the friction-free principle). A visitor typing a
 * Gmail address today fills in the whole form, solves a Turnstile challenge,
 * submits, and only THEN learns their address is unusable — with the Turnstile
 * widget reset, so the retry costs another challenge. Telling them the moment
 * they leave the field removes an entire round trip and a wasted challenge.
 *
 * ⚠ SILENT WHILE THE FIELD IS INCOMPLETE. A half-typed address is not an error,
 * it is an address in progress; warning on it would make the form nag at
 * everyone who tabs away mid-thought.
 */
export function checkBusinessEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  if (!email) return null;
  const at = email.lastIndexOf('@');
  // No `@`, or nothing after it yet ⇒ still being typed.
  if (at <= 0 || at === email.length - 1) return null;
  const domain = email.slice(at + 1);
  // No dot yet ⇒ still being typed.
  if (!domain.includes('.')) return null;
  if (FREE_EMAIL_DOMAINS.has(domain)) return FALLBACK_COPY.email_domain_blocked;
  return null;
}
